// ============================================================================
// CATALOGUE DES RÉGLAGES — qui a le droit d'écrire quoi, et ce qui a le droit de voyager.
//
// POURQUOI CE FICHIER EXISTE. La table `setting` est un sac de couples (nom, valeur) : n'importe quel
// nom s'y écrit, et le canal `settings` est `bi`. Trois conséquences, toutes constatées le 2026-08-15 :
//   · un nom mal orthographié s'écrit sans broncher et n'est jamais lu — l'écran de réglages ment sans
//     que rien ne le dise (§23.0) ;
//   · `setSetting` met TOUTE écriture en `outbox`, or la table n'a que `name` pour clé — ni magasin, ni
//     caisse. Sur un magasin à 15 caisses, le thème choisi sur la caisse 1 redescendait sur les 14
//     autres, comme l'état du tutoriel ou du volet latéral : quinze caisses pour une seule ligne ;
//   · pire, `api.key` — la clé d'authentification au back-office — partait par ce chemin : un secret
//     d'appareil publié dans le journal de synchronisation du magasin.
//
// Le catalogue est la réponse : il NOMME chaque réglage et sa portée. Ce qui n'y figure pas ne voyage
// pas (repli prudent), et ce qui est `bo_only` ne s'écrit pas depuis la caisse.
//
// ⚠️ IL VIT DANS LE CONTRAT parce que les DEUX bouts en dépendent : le back-office pour refuser une
// écriture illégitime et pour produire ce qu'il doit produire, la caisse pour savoir ce qu'elle a le
// droit de modifier et d'émettre. Une règle que les deux doivent connaître EST le contrat (§24.3bis) ;
// dupliquée de chaque côté, elle diverge au premier ajout.
// ============================================================================

/**
 * Qui écrit un réglage, et jusqu'où il voyage.
 *
 * · `bo_only`      — décidé au back-office. La caisse le LIT ; elle ne l'écrit jamais par le chemin
 *                    ordinaire. Le provisioning et la synchronisation passent par une voie NOMMÉE
 *                    (`applyProvisionedSetting`), comme la purge du journal côté serveur.
 * · `admin_tablet` — réglage de MAGASIN, modifiable en caisse par un administrateur, et partagé par
 *                    toutes les caisses du magasin (c'est bien le même magasin qui les porte).
 * · `device_local` — préférence ou état de CET appareil. Ne quitte jamais la tablette : le synchroniser
 *                    ferait s'écraser mutuellement les caisses sur une ligne unique.
 */
import { VOCABULARY_FIELDS, vocabularySettingNames } from './vocabulary';

export type SettingScope = 'bo_only' | 'admin_tablet' | 'device_local';

interface SettingDef {
  readonly scope: SettingScope;
  /** À quoi il sert — pour l'écran d'administration et pour qui lit ce fichier. */
  readonly note: string;
}

const bo = (note: string): SettingDef => ({ scope: 'bo_only', note });
const admin = (note: string): SettingDef => ({ scope: 'admin_tablet', note });
const local = (note: string): SettingDef => ({ scope: 'device_local', note });

/**
 * Le catalogue. ⚠️ Il décrit ce qui est APPLIQUÉ aujourd'hui, pas une cible : plusieurs réglages que le
 * SPEC destine à `bo_only` (fidélité, remises, validités — backoffice.md §12.9) sont ici en
 * `admin_tablet` parce que la caisse les édite RÉELLEMENT et que le back-office n'a pas encore l'écran
 * qui les remplacerait. Les basculer avant ce remplacement retirerait un réglage sans le rendre ailleurs.
 * Chaque cas porte la mention « §12.9 cible bo_only ».
 */
export const SETTINGS: Readonly<Record<string, SettingDef>> = {
  // ── IDENTITÉ LÉGALE DE L'ÉMETTEUR — scellée dans CHAQUE pièce et imprimée sur chaque ticket ──────
  // ⚠️ Aucune n'a d'écrivain en caisse (vérifié) : elles descendent, point. Tant que le provisioning ne
  // les pose pas, le repli est la chaîne VIDE — honnête (on n'invente pas une raison sociale), mais un
  // magasin scelle alors des pièces sans identité d'assujetti.
  'company.name': bo('Raison sociale — R13 ENC-TIK-SOC-NOM'),
  'company.address': bo('Adresse de l’établissement'),
  'company.zip': bo('Code postal'),
  'company.city': bo('Ville'),
  'company.country': bo('Pays (défaut FRANCE)'),
  'company.siret': bo('SIRET — identifie l’assujetti sur chaque pièce'),
  'company.naf': bo('Code NAF'),
  'company.tva': bo('N° TVA intracommunautaire'),
  'company.editor': bo('Éditeur du logiciel (mention légale)'),

  // ── LA TVA DU MAGASIN — un fait FISCAL de la société, jamais d'un article ─────────────────────────
  // ⚠️ LE RÉGIME EST UN FAIT D'IDENTITÉ, au même titre que le SIRET : allumé, la caisse vend TOUT à 0 % et
  //    chaque pièce porte la mention « TVA non applicable, art. 293 B du CGI ». Le code TVA « F » qu'il pose
  //    entre dans la ventilation SIGNÉE, donc un duplicata réimprime la mention même après la sortie de la
  //    franchise ; ses changements sont tracés au JET comme ceux de l'émetteur.
  'company.vat_franchise': bo('Franchise en base de TVA (art. 293 B du CGI) — toutes les ventes à 0 %, mention légale sur chaque pièce'),
  // ⚠️ LE TAUX PAR DÉFAUT S'ÉCRIT DANS LA FICHE À LA CRÉATION, il ne se devine jamais à la lecture : un article
  //    dont la source ne donne pas de taux (un catalogue EDI, un fichier sans colonne TVA) le reçoit de celui
  //    qui le crée. Le zéro d'une fiche veut alors dire 0 %, toujours. La caisse l'applique aussi à une ligne
  //    qui n'a pas d'article (une retouche).
  'vat.default_rate': bo('Taux de TVA d’un article créé sans taux dans une famille qui n’en porte pas, en % (« 20 », « 5,5 ») — vide = 20 %'),
  // ⚠️ IL RACCOURCIT LES MENUS DU SIÈGE, il n'interdit rien : un article repris ou reçu par EDI garde son taux, et la caisse
  //    ne choisit jamais de taux — elle applique celui de la fiche. Un taux porté par un article en service ne se retire pas.
  'vat.offered_rates': bo('Taux de TVA proposés dans les menus, en % séparés par « ; » (« 20;10;5,5 ») — vide = tous'),

  // ── CERTIFICATION NF525 — UNE seule famille de clés (arbitrage utilisateur 2026-08-15) ──────────
  // ⚠️ Le ticket lisait `nf.*` et l'écran `nf525.*` : le même fait sous deux noms. Aucun n'était produit,
  // donc l'unification n'a effacé AUCUNE valeur en base — c'est ce qui la rendait sûre maintenant et ne
  // le serait plus une fois un magasin rempli. Ce sont des faits d'ÉDITEUR : identiques pour toute la
  // flotte, ils viennent de la configuration de la Console, pas du dossier client.
  'nf525.category': bo('Catégorie NF525 imprimée sur le ticket'),
  'nf525.cert_number': bo('N° de certificat — imprimé sur le ticket ET affiché aux Réglages'),
  'nf525.cert_label': bo('Libellé de certification affiché aux Réglages'),

  // ── IDENTITÉ DE LA CAISSE — posée à l'APPAIRAGE par la voie nommée, jamais éditable ──────────────
  'register.gln': bo('GLN du magasin — racine des numéros de pièce'),
  'register.number': bo('N° de caisse — sépare les chaînes de signature'),
  'register.nf525': bo('Activation NF525 de cette caisse'),
  'register.env': bo('Environnement (prod / bêta)'),
  'store.code': bo('Code magasin'),
  'store.name': bo('Nom du magasin'),
  // ── COORDONNÉES DU POINT DE VENTE — ce que le client voit du magasin (≠ la société) ────────────────
  // ⚠️ `bo_only` TANT QUE LA CAISSE NE LES SAISIT PAS : le dessin les veut éditables en caisse, et ils passeront
  //    `admin_tablet` avec l'application qui offre la saisie — les déclarer modifiables en caisse avant ferait
  //    afficher au siège « Éditable en caisse » pour un geste qu'aucune caisse ne propose (§23.0).
  'store.phone': bo('Fixe ou mobile — nettoyé et enregistré au format international'),
  'store.email': bo('Adresse publique du magasin — distincte du compte d’envoi'),
  // ⚠️ LE RADICAL EAN EST UNE IDENTITÉ, AU MÊME TITRE QUE LE GLN — attribué UNE fois au provisioning
  // par la Console, FIGÉ ensuite. Il descend ici parce que le back-office ET la caisse doivent pouvoir
  // COMPOSER un gencode interne (« 2·RRRRR·NNNNNN·C ») sans rien demander à personne : la caisse crée
  // des articles hors ligne, et un code qu'il faudrait aller chercher au central ne serait pas
  // composable. ⚠️ Le changer réémettrait des codes déjà en rayon : jamais éditable à l'écran.
  'store.ean_radical': bo('Radical EAN du magasin (5 chiffres) — racine des gencodes internes'),

  // ── HORAIRES DU MAGASIN ──────────────────────────────────────────────────────────────────────────
  // Le SEUL réglage saisi de la journée : la bascule fiscale en est DÉRIVÉE (§9.4bis, maquette
  // §Magasin). Les horaires eux-mêmes ne bloquent jamais une vente — ils bornent des affichages.
  // ⚠️ LA CAISSE LES IMPRIME SUR LE TICKET (« Ouvert de 09:00 à 19:30 »), et c'est leur seul usage
  // en bas — décision utilisateur 2026-09-07. Ils ne servent NI aux rapports ni au graphique, qui se
  // bornent sur les ventes réelles : les horaires déclarés feraient disparaître une vente hors plage,
  // c'est-à-dire précisément celle qu'on vient regarder.
  'store.open': bo('Heure d’ouverture du magasin (HH:MM)'),
  'store.close': bo('Heure de fermeture du magasin (HH:MM) — après minuit = nocturne'),
  /**
   * LES JOURS OÙ CE MAGASIN EST OUVERT — `1` = lundi … `7` = dimanche, séparés par des virgules.
   *
   * ⚠️ 🔴 « JOUR OUVRÉ » NE VEUT PAS DIRE LUNDI-VENDREDI POUR UN COMMERCE. Le samedi est le meilleur
   * jour d'un SMS de soldes, et beaucoup de magasins ferment le lundi. Le sens bancaire aurait
   * systématiquement évité le samedi — l'inverse du besoin.
   * ⚠️ SON USAGE EST LE RATTRAPAGE D'UN ENVOI MANQUÉ, pas le blocage d'une vente : un magasin vend
   * quand il veut. Les horaires ci-dessus ne bloquent rien non plus, pour la même raison.
   * ⚠️ VIDE = AUCUNE RESTRICTION, délibérément. Inventer une fermeture le dimanche SUPPRIMERAIT un
   * envoi que l'exploitant attend ; ne rien restreindre en envoie un qu'il n'attendait peut-être pas.
   * Des deux erreurs, la seconde se voit et se corrige — la première est muette.
   */
  'store.open_days': bo('Jours d’ouverture (1=lundi … 7=dimanche, séparés par des virgules) — vide = tous'),
  /**
   * LE FUSEAU HORAIRE DU MAGASIN — un identifiant IANA (`Europe/Paris`, `America/Martinique`).
   *
   * ⚠️ 🔴 IL EXISTE PARCE QU'UN SERVEUR EN UTC ENVERRAIT DES SMS LA NUIT. Un envoi programmé « à 9 h »
   * doit partir à 9 h CHEZ LE MAGASIN ; sans ce réglage, la plateforme devrait supposer un fuseau, et
   * la supposition serait juste jusqu'au premier magasin hors métropole — où 9 h Paris fait 4 h du
   * matin sur place, sans que rien ne le signale, l'envoi réussissant parfaitement.
   * ⚠️ IL NE REMPLACE PAS LA JOURNÉE FISCALE : celle-ci est dérivée des horaires et n'a rien à voir
   * avec le fuseau — une caisse scelle dans l'heure locale de son système, ce réglage sert au SERVEUR.
   * ⚠️ NON POSÉ = `Europe/Paris` APPLIQUÉ, et l'écran doit le DIRE ainsi plutôt qu'afficher
   * « Europe/Paris » tout court : « choisi » et « par défaut » ne se corrigent pas de la même façon.
   */
  'store.timezone': bo('Fuseau horaire IANA du magasin (défaut Europe/Paris) — c’est lui qui donne l’heure d’un envoi programmé'),

  // ── JOURNÉE FISCALE ──────────────────────────────────────────────────────────────────────────────
  // ⚠️ `fiscal.day_cutoff` n'a AUCUN setter en caisse, et c'est délibéré (verrou NF525, §9.4bis) :
  // décaler la bascule changerait la journée d'imputation des pièces déjà scellées.
  // ⚠️ ELLE N'EST PLUS SAISIE NON PLUS AU BACK-OFFICE (2026-09-07) : elle est DÉRIVÉE des horaires
  // par `deriveDayCutoff` (core-nf525). La clé demeure — c'est elle qui descend et que tout le monde
  // lit — mais son écrivain est désormais l'écran des horaires, et lui seul. Deux saisies pour le
  // même fait divergeaient au premier oubli, sans que l'écart se voie nulle part.
  'fiscal.day_cutoff': bo('Heure de bascule de la journée fiscale (HH:MM) — DÉRIVÉE des horaires'),
  // ⚠️ LE VERROU DE CONTINUITÉ, EN DEUX CLÉS. Une bascule changée ne s'applique qu'à la PROCHAINE
  // journée fiscale : la nouvelle valeur attend ici avec son échéance, et chaque lecteur résout la
  // même chose au même instant (`resolveDayCutoff`). Sans elles, un changement en cours de journée
  // réattribuerait les pièces déjà scellées — deux journées se chevaucheraient.
  'fiscal.day_cutoff_pending': bo('Bascule EN ATTENTE (HH:MM) — s’applique à l’échéance ci-dessous'),
  'fiscal.day_cutoff_at': bo('Échéance de la bascule en attente (AAAAMMJJHHMMSS)'),
  'fiscal.closing_day': admin('Jour de clôture d’exercice — §12.9 cible bo_only'),
  'fiscal.closing_month': admin('Mois de clôture d’exercice — §12.9 cible bo_only'),
  'fiscal.ex_on': admin('Exercice exceptionnel actif — §12.9 cible bo_only'),
  'fiscal.ex_start': admin('Début d’exercice exceptionnel — §12.9 cible bo_only'),
  'fiscal.ex_end': admin('Fin d’exercice exceptionnel — §12.9 cible bo_only'),

  // ── PROGRAMME DE FIDÉLITÉ — §12.9 cible bo_only (règles de chaîne), édité en caisse aujourd'hui ───
  'fid.enabled': admin('Fidélité activée'),
  'fid.mode': admin('Mode de restitution (cashback / bon d’achat)'),
  'fid.conversion': admin('Taux de conversion'),
  'fid.tiers': admin('Paliers (JSON)'),
  'fid.tiers_on': admin('Paliers actifs'),
  'fid.use_from': admin('Seuil d’utilisation'),
  'fid.voucher_threshold': admin('Plafond d’émission du bon'),
  'fid.welcome_bonus': admin('Bonus de bienvenue'),
  'fid.allow_cumul': admin('Cumul autorisé avec d’autres remises'),
  'fid.forbid_immediate': admin('Interdit l’usage immédiat des points gagnés'),
  'fid.keep_tier': admin('Conservation du palier'),
  'fid.print_voucher': admin('Impression du bon'),
  'fid.protect_days': admin('Jours de protection du palier'),
  'fid.window_days': admin('Fenêtre de calcul (jours)'),
  // ⚠️ CAS RARE MAIS RÉEL (décision utilisateur 2026-08-31) : par défaut une ligne remisée ne crédite
  //    PAS la cagnotte — cumuler une remise ET du gain revient à payer deux fois le même geste
  //    commercial. Certains commerçants le veulent quand même : le réglage existe pour eux, il ne
  //    s'active pas par accident.
  'fid.earn_on_discounted': admin('Gains sur articles remisés (défaut : non)'),

  // ── REMISES, TITRES, WALLET — §12.9 cible bo_only ────────────────────────────────────────────────
  // ⚠️ LES MOTIFS DE REMISE NE SONT PLUS UN RÉGLAGE : ils vivent au référentiel `justification` (portée `discount`), où
  //    la vente enregistre leur CODE. L'ancienne liste `discount.reasons` n'est plus lue que par l'outil de reprise du BO.
  'discount.reason_required': admin('Justificatif obligatoire pour une remise'),
  'validity.credit_note': admin('Durée de validité d’un avoir'),
  'validity.gift': admin('Durée de validité d’une carte cadeau'),
  // ⚠️ `validity.deposit` était LU par la caisse (`voucher-validity.ts`) et le back-office sans figurer
  //    ici : hors catalogue, un réglage ne voyage pas et ne s'écrit pas — la durée retombait en silence
  //    sur le défaut du cœur. Le test de couverture ne l'a pas vu, la clé venant d'une table et non
  //    d'un littéral (audit 2026-08-31, F41).
  'validity.deposit': admin('Durée de validité d’un acompte'),
  'validity.loyalty': admin('Durée de validité d’un bon de fidélité'),
  'wallet.enabled': admin('Wallet activé'),
  // ⚠️ CATALOGUÉ parce qu'il AGIT désormais (2026-09-06) : il pose le consentement wallet sur une
  //    fiche créée en vente, et le back-office émet la carte au cycle suivant. Tant qu'il ne faisait
  //    rien, il n'avait rien à faire dans un contrat partagé.
  'wallet.auto': admin('Proposer la carte wallet automatiquement'),

  // ── LE GABARIT DE COUPON, PAR TYPE DE TITRE ─────────────────────────────────────────────────────
  //
  // 🔴 CE QUE CE RÉGLAGE DÉCIDE : un titre créé en caisse part-il dans le portefeuille du client, et
  //    sous quelle apparence. VIDE = il ne part pas — c'est le défaut, et c'est un défaut sûr.
  //
  // ⚠️ 🔴 `bo_only`, ET POUR UNE RAISON QUI N'EST PAS LE CONFORT : l'identifiant vient du
  //    FOURNISSEUR et se choisit dans une liste qu'il sert. Une caisse ne peut ni la lire ni la
  //    valider ; laissée écrire, elle y poserait un identifiant qu'aucun gabarit ne porte, et les
  //    envois échoueraient un par un sans que personne ne sache pourquoi.
  //
  // ⚠️ UN RÉGLAGE PAR TYPE, et non un seul pour tout le magasin : dans le portefeuille du client, un
  //    chèque cadeau et un bon de fidélité doivent se distinguer — sinon il ne sait pas ce qu'il tient.
  //
  // ⚠️ 🔴 L'ACOMPTE EST UNE CRÉANCE DU CLIENT SUR LE MAGASIN, pas un avantage commercial. Il est
  //    réglable parce que la décision a été prise de l'offrir ; le présenter dans un portefeuille de
  //    coupons le montre comme ce qu'il n'est pas, et c'est au commerçant de l'assumer en le réglant.
  'wallet.coupon_template.cadeau': bo('Gabarit de coupon wallet pour un chèque cadeau — vide = il ne part pas'),
  'wallet.coupon_template.fidelite': bo('Gabarit de coupon wallet pour un bon de fidélité — vide = il ne part pas'),
  'wallet.coupon_template.avoir': bo('Gabarit de coupon wallet pour un avoir — vide = il ne part pas'),
  'wallet.coupon_template.acompte': bo('Gabarit de coupon wallet pour un acompte — vide = il ne part pas'),

  // ── SMS — l'identité de l'expéditeur, propre au MAGASIN ──────────────────────────────────────────
  // ⚠️ `bo_only` : l'expéditeur (TPOA) est déclaré chez l'opérateur pour ce magasin ; une caisse qui
  //    pourrait l'écrire ferait partir des SMS sous un nom non autorisé, refusés à l'arrivée.
  // ⚠️ Le COMPTE d'envoi, lui, n'est PAS un réglage : il est rattaché au magasin par le staff en
  //    Console, et résolu côté serveur. L'exposer ici en ferait un paramètre — donc une prise.
  'sms.sender': bo('Nom affiché en tête des SMS (TPOA, déclaré chez l’opérateur)'),
  'sms.message_type': bo('Type de message SMS (PRM premium · -- medium · MND)'),

  // ── VENTE ────────────────────────────────────────────────────────────────────────────────────────
  'sale.loop': admin('Enchaîner les ventes'),
  'sale.credit_note_without_client': admin('Avoir autorisé sur vente sans client'),
  // ⚠️ Une POLITIQUE du magasin (« ici, on demande la carte »), donc décidée une fois pour toutes ses
  //    caisses ; et une option : refermer le sélecteur encaisse sans client, la vente n'est jamais bloquée.
  'sale.ask_client': admin('Proposer un client avant d’encaisser une vente sans client — une option, jamais une obligation'),
  'sale.default_receipt': admin('Justificatif par défaut'),
  'sale.view': local('Onglet de vente affiché — préférence de CET écran'),
  // ⚠️ Les préférences d'ENCAISSEMENT sont de CET appareil : un portable et une tablette du même magasin
  //    ne s'encaissent pas pareil, et le réglage de l'un ne doit pas s'imposer à l'autre.
  'sale.quick_cash': local('Montants rapides proposés au paiement en espèces, sur CETTE caisse'),
  'cash.denomination_photos': local('Photos des coupures au comptage du tiroir, sur CETTE caisse'),
  // ⚠️ Un terminal portable n'a souvent pas de tiroir, la caisse du comptoir en a un : éteint, aucune ouverture n'est
  //    tentée, donc aucun échec n'est annoncé à chaque vente en espèces.
  'cash.drawer': local('Tiroir-caisse branché sur CETTE caisse'),

  // ── TERMINAL DE PAIEMENT ─────────────────────────────────────────────────────────────────────────
  'tpe.use': admin('Utiliser un TPE'),
  // ⚠️ BIDIRECTIONNEL PAR DÉCISION (utilisateur, 2026-10) — ne passe PAS `bo_only` avec les familles du
  //    §12.9 : un terminal qui ne répond plus (un Concert qui change d'adresse, un TPE mobile hors réseau)
  //    empêcherait de finaliser TOUTE vente carte tant que la panne dure ; un opérateur HABILITÉ
  //    (`settings.write`) doit pouvoir desserrer l'obligation au comptoir, le siège la rétablit ensuite.
  'tpe.required': admin('TPE obligatoire (interdit la validation manuelle)'),
  'tpe.cb_client': admin('Ticket CB client (politique d’impression)'),
  'tpe.cb_merchant': admin('Ticket CB commerçant'),
  // ⚠️ Le TPE est un appareil BRANCHÉ À CETTE CAISSE : partager son adresse ferait dialoguer la caisse 2
  // avec le terminal du comptoir 1.
  'tpe.default': local('Terminal par défaut de CETTE caisse (JSON)'),
  'tpe.default_id': local('Identifiant du terminal par défaut'),
  'tpe.list': local('Terminaux découverts par CETTE caisse'),

  // ── IMPRESSION ───────────────────────────────────────────────────────────────────────────────────
  'print.type': admin('Type d’imprimante'),
  'print.cutter': admin('Mode de coupe'),
  'print.bitmap': admin('Rendu bitmap'),
  'print.throttle': admin('Temporisation d’impression'),
  'print.default_id': local('Imprimante par défaut de CETTE caisse'),
  // ⚠️ DE CETTE CAISSE, pas du magasin : un même magasin a des tablettes en 80 mm et des terminaux portables
  //    en 58 mm. Partagée, la largeur posée sur l'un mettrait les tickets de tous les autres à la mauvaise taille.
  'print.paper_width': local('Largeur du papier de l’imprimante de CETTE caisse (80 ou 58 mm)'),
  'print.list': local('Imprimantes découvertes par CETTE caisse'),

  // ── TICKET ───────────────────────────────────────────────────────────────────────────────────────
  'ticket.header': admin('En-tête libre du ticket'),
  'ticket.footer': admin('Pied de ticket'),
  'ticket.qr_link': admin('Lien du QR code'),
  // ⚠️ LA CLÉ NOMME LE MAUVAIS RÉFÉRENTIEL, ET ELLE RESTE : l'article porte `brand` ET `supplier`,
  //    deux listes distinctes ; ce commutateur a toujours masqué la MARQUE. La clé est persistée sur
  //    chaque caisse et descend d'ici — la renommer demanderait une migration de flotte pour un réglage
  //    qui fonctionne. Seul le libellé est corrigé (Forme J).
  'ticket.hide_supplier': admin('Masquer la marque'),
  'ticket.alteration_info': admin('Mentions du ticket de retouche'),

  // ── MESSAGERIE SORTANTE ──────────────────────────────────────────────────────────────────────────
  'mail.host': admin('Serveur SMTP'), 'mail.port': admin('Port SMTP'),
  'mail.user': admin('Utilisateur SMTP'),
  /**
   * 🔴 SECRET — IL NE QUITTE JAMAIS LA TABLETTE. Il était `admin_tablet`, donc SYNCHRONISÉ : il partait
   * en clair dans la file, arrivait en base au siège, et le canal `settings` étant `bi`, il redescendait
   * sur TOUTES les caisses du magasin. Deux commentaires de ce dépôt affirmaient pourtant le contraire
   * (« AUCUN SECRET NE VOYAGE ICI […] ni par les réglages synchronisés »).
   * ⚠️ MÊME TRAITEMENT QUE `api.key`, corrigé avant lui : un secret est `device_local`, sans exception.
   * ⚠️ CE QUE ÇA COÛTE, ET POURQUOI C'EST NUL AUJOURD'HUI : `device_local` veut dire que chaque caisse
   * saisit son propre mot de passe, là où un réglage de magasin se partageait. Le coût est nul tant que
   * la caisse n'envoie rien — `requestMail` n'a aucun appelant — et la question « qui envoie le
   * courrier, la caisse ou le siège ? » reste ouverte au cadrage des réglages back-office.
   * ⚠️ MESURÉ AVANT DE CORRIGER : absent des cinq magasins de la bêta, zéro événement au journal. Le
   * défaut était LATENT, donc rien à purger ni à faire tourner.
   */
  'mail.pass': local('Mot de passe SMTP — SECRET, ne voyage jamais'),
  'mail.from': admin('Expéditeur'), 'mail.ssl': admin('Connexion SSL au serveur SMTP'), 'mail.tls': admin('Passer en TLS après connexion'),
  'mail.pro': admin('Compte professionnel'), 'mail.credit': admin('Crédits restants'),
  'mail.subject': admin('Objet par défaut'), 'mail.body': admin('Corps par défaut'),

  // ── SOLDES — dates fixées par arrêté (2026-08-29) ────────────────────────────────────────────────
  // ⚠️ La fenêtre légale est CALCULÉE (règle nationale) ; ces réglages sont le REMPLACEMENT quand un
  // décret la déplace (reports 2020/2021) ou qu'une dérogation s'applique (frontaliers, outre-mer).
  // Valeur = JSON `{annee, start, end}` : l'arrêté ne vaut que pour SON année — la règle reprend seule
  // l'année suivante. Lu par le back-office uniquement (les caisses reçoivent les promotions datées).
  'clearance.winter_dates': bo('Dates d’arrêté des soldes d’hiver — JSON {annee, start, end}'),
  'clearance.summer_dates': bo('Dates d’arrêté des soldes d’été — JSON {annee, start, end}'),

  // ── DIVERS MAGASIN ───────────────────────────────────────────────────────────────────────────────
  'deposit.notes_only': admin('Remise en banque : billets seulement'),
  'alteration.catalog': admin('Catalogue des retouches'),
  // ⚠️ BIDIRECTIONNEL PAR DÉCISION (utilisateur, 2026-10) — réglé en caisse comme au siège. Et un réglage
  //    de MAGASIN, pas une préférence d'écran : en caisse il ne se modifie qu'avec `settings.write`, même
  //    s'il est rangé dans l'onglet Affichage à côté de préférences qui, elles, se règlent sans droit.
  'app.lock_min': admin('Délai de verrouillage de session (minutes)'),

  // ── LIAISON AU BACK-OFFICE ───────────────────────────────────────────────────────────────────────
  // ⚠️ `api.key` est un SECRET D'APPAREIL : le pousser publierait la clé d'authentification du magasin
  // dans son propre journal de synchronisation, d'où elle redescendrait sur toutes les caisses.
  'api.key': local('Clé d’API de CETTE caisse — SECRET, ne voyage jamais'),
  'api.bo_url': local('URL du back-office'),
  'api.store_url': local('URL du store privé'),
  'api.shop': local('Identifiant boutique (mises à jour)'),
  'api.delay': admin('Période du heartbeat'),
  'api.sync_auto': admin('Synchronisation automatique'),
  // ⚠️ UNE SEULE CLÉ POUR CE FAIT. `api.last_sync` a existé à côté, sans écrivain : l'écran la
  //    lisait et affichait « Non synchronisé » à vie. Deux clés voisines pour « la dernière fois où
  //    l'on a parlé au siège » divergent toujours — celle-ci est la seule, et elle arme AUSSI le
  //    verrou hors ligne (§13.1quater), ce qui interdit de la renommer sans migration (Forme J).
  'api.last_sync_ok_at': local('Dernière synchronisation RÉUSSIE (état local)'),
  // ⚠️ CE QUI DISTINGUE UNE CAISSE QUI SE REMPLIT D'UNE CAISSE À JOUR. Tant qu'elle est absente, la
  // caisse n'a JAMAIS reçu sa base entière : ses cycles descendent alors SANS PLAFOND (une caisse sans
  // son catalogue ne peut pas travailler). Posée au premier cycle qui ne laisse rien derrière lui — donc
  // un amorçage coupé par une panne réseau reprend sans plafond au démarrage suivant, tout seul.
  'api.initial_full_done': local('Première réception COMPLÈTE effectuée (état local)'),
  'api.offline_rule': admin('Règle de tolérance hors ligne'),
  'api.offline_warn_days': admin('Jours avant avertissement hors ligne'),
  'api.offline_lock_days': admin('Jours avant verrouillage hors ligne'),
  // ⚠️ 🔴 `bo_only` ET NON APPLIQUÉ, les deux à la fois (décision utilisateur). Un utilisateur
  //    de caisse ne doit pas pouvoir desserrer un blocage d'encaissement : la décision appartient au siège,
  //    comme le kill-switch de la Console. Et aucun code de caisse ne le lit — son UTILITÉ même (refuser
  //    d'encaisser dès que le serveur est injoignable, là où §13.1quater tolère puis bloque à N jours) est
  //    soumise à débat interne. Tant que ce n'est pas statué, la NOTE le dit à l'écran du BO plutôt que de
  //    laisser croire à une règle en vigueur ; le jour où il est retenu, il se branche sur l'état réseau
  //    partagé (`@orgazone/net-status`), jamais sur un délai.
  'api.block_offline': bo('Bloquer l’encaissement quand la caisse est hors ligne — ⚠️ non appliqué par la caisse aujourd’hui : l’utilité de cette mécanique est en débat interne'),
  // ⚠️ LE SENS DE SYNCHRONISATION DU MAGASIN (§12.9.1, curseur 1) — `bo_only` : c'est le super-admin qui le décide, à la
  //    création puis dans la fiche du magasin à la Console. Il descend comme tout réglage ; la caisse le LIT
  //    (`storeSyncMode`) et le serveur aussi, qui refuse alors les réglages qu'une caisse ferait remonter.
  'api.sync_mode': bo('Sens de synchronisation du magasin — « mono » : le siège décide de tous les réglages du magasin, la caisse les lit ; « bi » : la caisse les modifie aussi et les fait remonter'),

  // ── APPAREIL & MISES À JOUR — propres à CETTE tablette ───────────────────────────────────────────
  'app.device_id': local('Identifiant de l’appareil'),
  'app.device_key': local('Clé d’appareil (store privé)'),
  'app.channel': local('Canal de mise à jour'),
  'app.group_key': local('Clé de groupe de déploiement'),
  'app.update_group': local('Groupe de mise à jour'),
  'app.update_attempt_count': local('Tentatives de mise à jour'),
  'app.update_attempt_target': local('Version visée par la tentative'),
  'app.last_build': local('Dernier build démarré (détection de mise à jour)'),
  // ⚠️ LE GROUPE DE MISE À JOUR SE CHOISIT DANS LA CONSOLE, par magasin et par application (§32.6ter) :
  //    ni le back-office ni la caisse ne le modifient. La valeur est le jeton d'INSCRIPTION du groupe au
  //    store — public, c'est celui d'un QR — et jamais sa clé : chaque caisse la demande elle-même au
  //    store. Vide = le flux public de l'application.
  // ⚠️ `ref` ET NON `token` DANS LE NOM : la garde des secrets juge les NOMS (`secrets-ne-voyagent-pas`), et ce
  //    jeton-là est public — l'appeler `token` le ferait passer pour un secret qui ne doit pas voyager.
  'update.group_ref': bo('Groupe de mise à jour des caisses, choisi dans la Console — vide = flux public de l’application'),
  // ⚠️ La caisse ne suit la Console que quand sa cible CHANGE : comparer à la clé de groupe en vigueur
  //    défairait à chaque synchronisation un enrôlement fait à la main depuis les Réglages.
  'app.group_target': local('Dernier groupe désigné par la Console et appliqué par CETTE caisse'),
  // ⚠️ GARDÉE À PART DE `api.store_url` : quand la Console choisit le store du fabricant, l'adresse en vigueur est
  //    vide (la caisse n'interroge pas le store privé) ; c'est celle-ci qui la rétablit si la Console change d'avis.
  'app.console_store_url': local('Adresse du store privé remise par la Console à la mise en service'),
  'register.device_id': local('Identifiant matériel lié au n° de caisse'),
  'pairing.done': local('Appairage effectué'),
  // ⚠️ POSÉS PAR LA MISE EN SERVICE, ET C'EST CE QUI REND LA REPRISE AUTOMATIQUE : une tablette qui remplace une
  //    caisse perdue arrive vide sous un n° qui a déjà vendu. La NUMÉROTATION se reprend avant la première vente
  //    (la caisse reste fermée tant que ce n'est pas fait), l'HISTOIRE redescend au premier cycle qui le peut.
  'pairing.counters_pending': local('Numérotation de la caisse à reprendre au serveur avant la première vente'),
  'pairing.history_pending': local('Historique de la caisse à faire redescendre (non signé)'),
  // ⚠️ Gardée jusqu'au scellement du JET 220 : une reprise relancée ne trouve plus rien à relever, et sans elle le JET
  //    qui explique le saut de numérotation à l'auditeur ne serait jamais écrit.
  'pairing.numbering_jet': local('Reprise de numérotation à déclarer au journal fiscal (JET 220)'),

  // ── PRÉFÉRENCES D'AFFICHAGE — jamais partagées : quinze caisses, quinze écrans ────────────────────
  'app.theme': local('Thème clair / sombre de CET écran'),
  'app.sidebar_expanded': local('Volet latéral déplié'),
  'app.text_scale': local('Taille du texte'),
  'app.lang': local('Langue de l’interface'),
  'app.online': local('Autoriser les appels réseau'),
  'tours.done': local('Visites guidées vues, par vendeur, sur CETTE caisse'),
  // ⚠️ Jamais un compte de l'éditeur : présélectionné, il recevait les mots de passe des vendeurs comme des échecs.
  'app.last_operator': local('Dernier opérateur connecté sur CETTE caisse, proposé à la connexion'),

  // ── ÉTAT DU JET — propre à CETTE caisse : son journal d'événements a ses propres compteurs ───────
  // ⚠️ Partagé, il ferait taire une installation : la caisse 2 verrait « version déjà déclarée » parce
  // que la caisse 1 l'a déclarée, et l'événement R19 250/255 manquerait à son journal.
  'jet.last_version': local('Dernière version applicative déclarée au JET de CETTE caisse'),
  'jet.last_online': local('Dernier passage en ligne déclaré au JET'),
  // ⚠️ Les JET 130 (habilitations) et 410 (identité de l'assujetti) qu'une page de synchronisation n'a pas pu écrire —
  //    retentés à la page suivante : la détection ne se rejoue pas, ils seraient perdus pour toujours.
  'jet.pending_sync': local('Événements du journal fiscal nés d’une synchronisation, à écrire'),

  // ── LA DEVISE DU MAGASIN (2026-08-25) — descendue à la CRÉATION, jamais modifiable ensuite ───────
  // ⚠️ `bo_only` AU SENS STRICT : ces trois lignes sont écrites UNE FOIS par le provisioning de la
  // Console, et rien — ni la caisse, ni le back-office — ne doit les réécrire. Elles donnent leur sens
  // à TOUS les montants déjà scellés : les changer réinterpréterait l'historique entier, en silence.
  // ⚠️ La caisse les LIT au démarrage (`services/devise.ts`) parce qu'elle encaisse HORS LIGNE : une
  // devise qu'il faudrait aller chercher au central ne serait pas là au tunnel de paiement.
  'currency.code': bo('Devise du magasin (code ISO) — figée à la création, elle interprète tous les montants'),
  'currency.fraction': bo('Décimales de la devise — 2 pour l’euro, 0 pour le yen, 3 pour les dinars'),
  // ⚠️ Le code NUMÉRIQUE n'est pas un doublon du code alpha : c'est le protocole du TERMINAL DE
  // PAIEMENT qui l'exige, et le terminal encaisse sans réseau.
  'currency.numeric': bo('Devise du magasin (code ISO numérique) — exigé par le terminal de paiement'),

  // ── BROUILLONS DE CAISSE — état de travail local, jamais partagé ─────────────────────────────────
  // Index des photos d'articles rapides prises SUR cette caisse : les fichiers vivent sur SON disque,
  // partager l'index ferait pointer les autres caisses vers des chemins qui n'existent pas chez elles.
  'quick.photos': local('Index des photos d’articles rapides de CETTE caisse'),
  // ⚠️ L'ORDRE LOCAL L'EMPORTE SUR CELUI DU BACK-OFFICE (décision utilisateur 2026-08-25), À UNE BORNE PRÈS : le siège
  // pose le bon défaut, l'opérateur qui range ses tuiles devant lui sait ce qu'il vend le plus. Le rangement porte la
  // GÉNÉRATION sous laquelle il a été fait, et un RENVOI du back-office (génération plus récente) le jette — le seul moyen
  // de corriger une caisse réarrangée par erreur ou malveillance (arbitrage A2 du studio). `device_local` au sens
  // fort : il ne remonte pas et ne se partage pas, deux comptoirs n'ont ni le même flux ni la même main.
  'quick.tree_order': local('Rangement de l’arbre des articles rapides fait sur CETTE caisse — jeté au renvoi du back-office'),
  'draft.opening': local('Brouillon de fond de caisse (ouverture)'),
  'draft.closing': local('Brouillon de comptage (clôture)'),

  // ── LE VOCABULAIRE — le mot de chaque champ d'article, par langue (`vocabulary.ts`) ───────────────
  // ⚠️ DÉRIVÉ du catalogue des champs, jamais recopié : 22 champs × 3 langues × 2 couches. Les DEUX
  //    couches sont `bo_only` — `vocab_default.*` est posé par la CONSOLE (le mot du métier, propagé),
  //    `vocab.*` par le COMMERÇANT au back-office (sa surcharge) — et la caisse ne fait que LIRE. En
  //    `admin_tablet`, une caisse qui pousserait la ligne écraserait le choix du back-office (le canal
  //    est `bi`, dernier écrivain gagnant) : c'est `bo_only` qui rend la garantie « la surcharge ne
  //    peut pas être écrasée par une caisse » vraie aux deux étages, la caisse et le serveur.
  ...vocabularySettings(),
};

/** Les entrées de vocabulaire du catalogue — une par clé persistée, la note nomme le champ et la couche. */
function vocabularySettings(): Record<string, SettingDef> {
  const wordOf = new Map(VOCABULARY_FIELDS.map((f) => [f.key, f.defaults.fr] as const));
  const out: Record<string, SettingDef> = {};
  for (const k of vocabularySettingNames()) {
    out[k.name] = bo(k.layer === 'default'
      ? `Vocabulaire — « ${wordOf.get(k.field)} » : le mot du métier (${k.lang}), posé par la Console`
      : `Vocabulaire — « ${wordOf.get(k.field)} » : le mot choisi par le magasin (${k.lang})`);
  }
  return out;
}

/** Le réglage est-il connu du catalogue ? (un nom absent est un nom que personne ne lit) */
export function isKnownSetting(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(SETTINGS, name);
}

/**
 * Portée d'un réglage. ⚠️ Un nom INCONNU est traité comme `device_local` : le repli le plus sûr est
 * qu'il ne voyage pas — le back-office ne connaît pas ce nom, donc rien n'a à le recevoir. L'appelant
 * DOIT le signaler (un réglage hors catalogue est une anomalie, pas un cas normal).
 */
export function settingScope(name: string): SettingScope {
  return SETTINGS[name]?.scope ?? 'device_local';
}

/** Le réglage voyage-t-il entre la caisse et le back-office ? (`device_local` = jamais) */
export function isSyncedSetting(name: string): boolean {
  return isKnownSetting(name) && settingScope(name) !== 'device_local';
}

/** La caisse a-t-elle le droit de l'écrire par le chemin ORDINAIRE ? (`bo_only` = non) */
export function isTabletWritableSetting(name: string): boolean {
  return settingScope(name) !== 'bo_only';
}

/** Réglages d'une portée donnée — sert les écrans d'administration et le provisioning. */
export function settingsOfScope(scope: SettingScope): string[] {
  return Object.keys(SETTINGS).filter((n) => SETTINGS[n]!.scope === scope);
}

/** Le sens de synchronisation d'un magasin (§12.9.1). */
export type StoreSyncMode = 'mono' | 'bi';

/** Le réglage qui le porte, posé par la Console. */
export const STORE_SYNC_MODE_SETTING = 'api.sync_mode';

/**
 * LE SENS QUE LA CAISSE ET LE SERVEUR APPLIQUENT, lu dans le réglage `api.sync_mode`.
 * ⚠️ ABSENT, ILLISIBLE : « bi ». C'est ce que toutes les caisses faisaient avant que le sens descende, et c'est le
 *    défaut de la Console : un magasin à qui le siège n'a rien transmis n'a rien décidé, on ne le fige pas en lecture
 *    seule sur une valeur qu'on n'a pas. Le « mono » s'applique dès que la Console l'a écrit.
 * ⚠️ UNE SEULE LECTURE, au contrat : la caisse qui grise ses réglages et le serveur qui refuse leur remontée doivent
 *    juger le même magasin de la même façon, sinon l'un écrit ce que l'autre jette.
 */
export function storeSyncMode(value: string | null | undefined): StoreSyncMode {
  return (value ?? '').trim() === 'mono' ? 'mono' : 'bi';
}

/**
 * La caisse peut-elle écrire ce réglage dans un magasin de ce sens ? En « mono », aucun réglage du MAGASIN : le siège
 * décide de tous ; seules les préférences de l'appareil (`device_local`) restent à la caisse.
 */
export function isTabletWritableIn(name: string, mode: StoreSyncMode): boolean {
  if (!isTabletWritableSetting(name)) return false;
  return mode === 'bi' || settingScope(name) !== 'admin_tablet';
}

/**
 * LES INTERRUPTEURS — les réglages lus comme un booléen, et ce que vaut leur ABSENCE.
 *
 * ⚠️ IL VIT DANS LE CONTRAT parce qu'un interrupteur jamais réglé est lu à TROIS endroits : la caisse
 * applique son défaut, le serveur calcule avec (paliers, wallet), et l'écran du back-office qui le montre
 * doit afficher le MÊME état. Écrit de chaque côté, le défaut diverge sans bruit — chaque écran reste
 * cohérent avec lui-même, et c'est l'opérateur qui découvre que « Vente en boucle » tourne en caisse
 * pendant que le siège l'affiche éteint.
 * ⚠️ UN MAGASIN QUI N'A JAMAIS TOUCHÉ UN INTERRUPTEUR N'A RIEN DÉCIDÉ : l'écran montre ce que la caisse
 * FAIT, jamais une case éteinte faute d'information.
 * ⚠️ LA LISTE EST FERMÉE PAR LE TYPE (`SwitchName`) : lire un réglage libre comme un booléen, ou dessiner
 * un interrupteur sur une valeur libre, ne compile pas. Chaque nom doit aussi figurer au catalogue
 * ci-dessus — le banc du contrat le vérifie.
 */
export const SWITCH_DEFAULTS = {
  // ── Vente & encaissement ──
  'sale.loop': true,
  'sale.credit_note_without_client': false,
  'discount.reason_required': true,
  'tpe.use': true,
  // ⚠️ Allumé, il interdit toute validation carte sans passage par le terminal : un choix, pas un défaut.
  'tpe.required': false,
  'deposit.notes_only': false,
  'sale.quick_cash': true,
  'sale.ask_client': false,
  'cash.denomination_photos': true,
  'cash.drawer': true,
  // ── TVA de la société ──
  // ⚠️ ÉTEINT QUAND IL EST ABSENT : un magasin qui n'a rien déclaré collecte la TVA au taux de ses articles. Le
  //    supposer en franchise ferait vendre sans TVA chez qui est tenu de la collecter.
  'company.vat_franchise': false,
  // ── Ticket, impression, exercice ──
  'ticket.hide_supplier': false,
  'print.bitmap': false,
  'fiscal.ex_on': false,
  // ── Messagerie de la caisse ──
  'mail.ssl': true,
  'mail.tls': false,
  'mail.pro': false,
  // ── Liaison au back-office ──
  'api.sync_auto': true,
  'api.block_offline': false,
  // ⚠️ ÉTEINTE QUAND ELLE EST ABSENTE : un magasin provisionné avant la règle n'a jamais reçu ses seuils,
  //    et la caisse ne verrouille pas sur des seuils qu'elle n'a pas.
  'api.offline_rule': false,
  // ── Fidélité ──
  'fid.enabled': true,
  'fid.allow_cumul': true,
  'fid.print_voucher': true,
  'fid.forbid_immediate': false,
  // ⚠️ Une ligne remisée ne crédite pas la cagnotte : cumuler la remise ET le gain paierait deux fois le
  //    même geste commercial (décision utilisateur). Le réglage existe pour qui le veut.
  'fid.earn_on_discounted': false,
  // Le programme livré est à paliers, et le statut acquis est protégé.
  'fid.tiers_on': true,
  'fid.keep_tier': true,
  // ── Wallet ──
  'wallet.enabled': true,
  'wallet.auto': true,
  // ── État de CETTE caisse ──
  'app.online': true,
  'app.sidebar_expanded': true,
  'pairing.done': false,
  'pairing.counters_pending': false,
  'pairing.history_pending': false,
  'api.initial_full_done': false,
} as const satisfies Readonly<Record<string, boolean>>;

export type SwitchName = keyof typeof SWITCH_DEFAULTS;

/** Le réglage est-il un interrupteur ? */
export function isSwitchSetting(name: string): name is SwitchName {
  return Object.prototype.hasOwnProperty.call(SWITCH_DEFAULTS, name);
}

/**
 * L'interrupteur est-il ALLUMÉ ? — la seule lecture, pour la caisse, le serveur et le back-office.
 *
 * ⚠️ `'1'` SEUL VAUT « allumé » une fois le réglage posé : c'est ce que la caisse écrit (`setSettingBool`)
 * et ce que le back-office écrit. Une lecture plus tolérante d'un côté (« tout sauf '0' », « tout sauf
 * 'false' ») déclarerait allumé un réglage que l'autre lit éteint.
 * ⚠️ ABSENT (`null`/`undefined`) vaut le défaut déclaré. Une chaîne VIDE ou inconnue vaut éteint : elle a
 * été écrite, donc quelqu'un a décidé quelque chose, et ce n'était pas « 1 ».
 */
export function switchOn(name: SwitchName, value: string | null | undefined): boolean {
  return value == null ? SWITCH_DEFAULTS[name] : value === '1';
}

/**
 * LES CLÉS DU PROGRAMME DE FIDÉLITÉ — dérivées du catalogue, jamais recopiées.
 *
 * ⚠️ DÉRIVÉES : une seconde liste écrite à la main divergerait au premier réglage ajouté, et le BO
 * refuserait alors une clé que la caisse édite (Forme A du catalogue des défauts). Le préfixe est la
 * seule chose à connaître.
 */
export const FID_SETTING_KEYS: readonly string[] = Object.keys(SETTINGS).filter((k) => k.startsWith("fid."));

/** Un palier, tel qu'il est rangé en JSON sous `fid.tiers` (montants en EUROS, taux en %, tout en chaînes). */
export interface FidTier {
  id: string;
  /** Libellé affiché (« Silver ») — donnée, pas jeton de charte. */
  l: string;
  color: string;
  /** Borne basse du cumul d'achats, en EUROS. */
  from: string;
  /** Borne haute, en euros ; vide = pas de plafond (dernier palier). */
  to: string;
  /** Taux d'acquisition du palier, en POURCENTS. */
  rate: string;
}

/**
 * LES DÉFAUTS DES PALIERS — déclarés UNE fois, lus par les deux bouts.
 *
 * ⚠️ POURQUOI DANS LE CONTRAT. Ils existaient en TROIS copies (caisse, écran du back-office, assistant
 * de la Console), qu'aucun test ne comparait (audit fidélité 2026-08-31). Elles concordaient ce jour-là ;
 * rien ne les y obligeait le lendemain — et un barème qui diffère d'un écran à l'autre crédite les
 * clients à deux taux selon l'endroit où on l'a réglé. Le serveur ayant désormais besoin des mêmes
 * valeurs pour CALCULER les paliers, la duplication devenait une divergence en attente (Forme A).
 * ⚠️ Ce n'est pas une CIBLE mais un REPLI : ce sont les valeurs appliquées quand le magasin n'a jamais
 * rien réglé. Le back-office les affiche marquées « par défaut », précisément pour ne pas faire croire
 * à un choix.
 */
export const FID_TIER_DEFAULTS = {
  // ⚠️ Les interrupteurs des paliers (`fid.tiers_on`, `fid.keep_tier`) vivent avec TOUS les autres, dans
  //    `SWITCH_DEFAULTS` : un défaut d'interrupteur rangé à part serait une seconde table du même fait.
  /** Fenêtre glissante de cumul, en jours. */
  window_days: '365',
  /** Durée de garantie du statut, en jours (⚠️ une DURÉE, pas un montant — cf. `TierRules.protectDays`). */
  protect_days: '365',
  tiers: [
    // ⚠️ Couleurs de DONNÉE (paliers réglables à l'écran, remplaçables par le back-office), pas des
    //    jetons de charte : argent / or / platine évoquent le métal, pas l'interface.
    { id: 'silver', l: 'Silver', color: '#9AA3AB', from: '0', to: '1500', rate: '2.0' },
    { id: 'gold', l: 'Gold', color: '#C79A3A', from: '1501', to: '2500', rate: '3.0' },
    { id: 'plat', l: 'Platinium', color: '#5B6CFF', from: '2501', to: '', rate: '5.0' },
  ] as FidTier[],
} as const;

/**
 * LES DÉCIMALES DE LA DEVISE DU MAGASIN, lues au réglage `currency.fraction` — 2 quand il est absent, vide ou illisible.
 * ⚠️ UNE LECTURE, PAS QUATRE : le siège la recopiait à chaque écran qui convertit un montant, et l'une des copies lisait une
 *    valeur VIDE comme « zéro décimale » (`Number('')` vaut 0) — un magasin en euros y aurait compté en unités entières.
 * ⚠️ BORNÉE À 4 : aucune devise n'en porte davantage ; au-delà, c'est une valeur fausse, pas une devise.
 */
export function currencyFractionOf(value: string | null | undefined): number {
  const text = (value ?? '').trim();
  const fraction = Number(text);
  return text !== '' && Number.isInteger(fraction) && fraction >= 0 && fraction <= 4 ? fraction : 2;
}

/** Le bonus de bienvenue appliqué quand le magasin ne l'a jamais réglé — un montant en unités de la devise, comme le réglage. */
export const FID_WELCOME_BONUS_DEFAULT = '5';

/** Le mode de restitution porté par chaque écriture de cagnotte (la valeur de `loyalty_ledger.mode`). */
export type LoyaltyLedgerMode = 'cashback' | 'bon_achat';

/**
 * Le mode de restitution du programme, lu au réglage `fid.mode` : seul « cashback » vaut cashback, tout le reste — absent
 * compris — est le bon d'achat, le mode par défaut du programme.
 */
export function loyaltyLedgerMode(modeSetting: string | null | undefined): LoyaltyLedgerMode {
  return modeSetting === 'cashback' ? 'cashback' : 'bon_achat';
}

/**
 * LE BONUS DE BIENVENUE D'UNE FICHE NEUVE, tel que le programme du magasin le règle — `null` quand il n'y en a pas.
 *
 * ⚠️ UNE RÈGLE POUR LA CAISSE ET LE SIÈGE : une fiche créée au comptoir et une fiche créée au back-office reçoivent le
 *    même bonus, ou aucune n'en reçoit. Écrite des deux côtés, la règle aurait fini par créditer selon l'endroit.
 * ⚠️ LA LECTURE DU MONTANT EST TOLÉRANTE, comme celle de la caisse : « 5 », « 5,00 » et « 5.00 » valent cinq unités de la
 *    devise ; une valeur illisible vaut zéro — donc aucun bonus. Les bornes du réglage se contrôlent à son ÉCRITURE, au
 *    siège ; un réglage descendu n'est pas une saisie à refuser.
 * ⚠️ L'ÉCHELLE EST CELLE DE LA DEVISE DU MAGASIN (`scale`) : « 500 » vaut 500 yens, pas 50 000.
 * ⚠️ RIEN QUAND LE PROGRAMME EST ÉTEINT : `fid.enabled` lu par `switchOn`, le défaut déclaré au contrat.
 */
export function welcomeBonusOf(
  settings: { enabled: string | null | undefined; bonus: string | null | undefined; mode: string | null | undefined },
  scale: number,
): { bonusMinor: number; mode: LoyaltyLedgerMode } | null {
  if (!switchOn('fid.enabled', settings.enabled)) return null;
  const amount = Number.parseFloat(String(settings.bonus ?? FID_WELCOME_BONUS_DEFAULT).replace(',', '.'));
  const bonusMinor = Number.isFinite(amount) ? Math.round(amount * 10 ** scale) : 0;
  return bonusMinor > 0 ? { bonusMinor, mode: loyaltyLedgerMode(settings.mode) } : null;
}

/**
 * LE BARÈME DES PALIERS DU MAGASIN, tel que le réglage `fid.tiers` le range — le barème par défaut quand il est absent,
 * vide ou illisible.
 * ⚠️ UNE LECTURE POUR TOUS : le classement des clients (serveur), leur pastille (caisse et siège) et les statistiques
 *    lisent le même barème ; écrite à chaque endroit, la lecture a déjà existé en deux copies.
 * ⚠️ `JSON.parse('null')` NE LÈVE PAS : seule une liste non vide est un barème, comme à la caisse (`getSettingJson`).
 */
export function tierScaleOf(tiersSetting: string | null | undefined): readonly FidTier[] {
  if (!tiersSetting) return FID_TIER_DEFAULTS.tiers;
  try {
    const read = JSON.parse(tiersSetting) as unknown;
    return Array.isArray(read) && read.length > 0 ? (read as FidTier[]) : FID_TIER_DEFAULTS.tiers;
  } catch {
    // Barème illisible : le défaut, comme pour le classement — l'écran des réglages, lui, le dit.
    return FID_TIER_DEFAULTS.tiers;
  }
}

/** Un palier tel qu'il s'affiche : son libellé et sa couleur, lus au barème du magasin. */
export interface TierDisplay {
  label: string;
  /** Couleur de DONNÉE du barème (`#C79A3A`) ; `null` quand le palier n'y figure pas — l'écran prend un ton neutre. */
  color: string | null;
}

/**
 * LE PALIER D'UN CLIENT, TEL QU'IL S'AFFICHE — libellé et couleur du barème `fid.tiers` du magasin.
 *
 * ⚠️ POURQUOI ICI, DANS LE CONTRAT. La caisse et le back-office affichent le même palier, sur la fiche et
 * dans la liste. Le back-office le peignait d'après une table en dur indexée `S`/`G`/`P` alors que le
 * serveur écrit l'IDENTIFIANT du barème (`silver`, `gold`, `plat`) : aucun palier réel n'y correspondait,
 * et la fiche affichait « gold » en gris. La caisse ne l'affichait pas du tout. Une seule lecture, ici.
 * ⚠️ LE LIBELLÉ EST UNE DONNÉE, PAS UN TEXTE : le magasin le règle (« Silver » peut devenir « Argent »), il
 *    part en base sous `fid.tiers` — le traduire ferait dire au client autre chose que ce que le magasin a
 *    choisi.
 * ⚠️ REPLIS, DANS L'ORDRE : le barème du magasin, sinon le barème par défaut (un magasin qui n'a jamais rien
 *    réglé classe quand même ses clients), sinon l'identifiant BRUT — un palier qu'on n'explique plus vaut
 *    mieux qu'un palier effacé. Un barème illisible (JSON cassé, mauvaise forme) retombe sur le défaut,
 *    comme la caisse et le serveur qui CLASSENT les clients.
 * ⚠️ `null` SANS PALIER : aucune pastille plutôt qu'un palier inventé.
 */
export function tierDisplay(tierId: string | null | undefined, tiersSetting: string | null | undefined): TierDisplay | null {
  const id = (tierId ?? '').trim();
  if (!id) return null;
  const bareme = tierScaleOf(tiersSetting);
  const trouve = bareme.find((x) => x?.id === id) ?? FID_TIER_DEFAULTS.tiers.find((x) => x.id === id);
  if (!trouve) return { label: id, color: null };
  return { label: String(trouve.l ?? '').trim() || id, color: String(trouve.color ?? '').trim() || null };
}
